<script setup lang="ts">

import ConveyorTLBR from '@/components/Sections/Resources/Elements/Conveyor/ConveyorTLBR.vue'

import { defineProps, computed } from 'vue'
import type { PropType } from 'vue';
import { useGameStore } from '@/stores/gameStore.ts'
import type OreConversion from '@/types/OreConversion.ts'
import ConveyorTRBL from '@/components/Sections/Resources/Elements/Conveyor/ConveyorTRBL.vue'
import ConveyorTB from '@/components/Sections/Resources/Elements/Conveyor/ConveyorTB.vue'
import Ore from '@/components/Sections/Resources/Elements/PlanetOre/Ore.vue'
import { usePlanetStore } from '@/stores/planetStore.ts'
import { CDN } from '@/models/cdn.ts'
import GenericIcon from '@/components/Elements/Images/Icons/GenericIcon.vue'
import StyledImage from '@/components/Elements/Images/StyledImage.vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const gameStore = useGameStore()
const planetStore = usePlanetStore()

const props = defineProps({
  left: {
    type: Number,
    required: true
  },
  top: {
    type: Number,
    required: true
  },
  height: {
    type: Number,
    required: true
  },
  width: {
    type: Number,
    required: true
  },
  oreLeft: {
    type: Number,
    required: true
  },
  oreRight: {
    type: Number,
    required: true
  },
  oreProcessed: {
    type: Number,
    required: true
  },
  conversionLeft: {
    type: Object as PropType<OreConversion>,
    required: true
  },
  conversionRight: {
    type: Object as PropType<OreConversion>,
    required: true
  },
  processed: {
    type: Object as PropType<OreConversion>,
    required: true
  },
  conveyorType: {
    type: String,
    required: false,
    default: 'level1'
  },
  buildingType: {
    type: String,
    required: true
  },
  debug: {
    type: Boolean,
    required: false,
    default: false
  }
})

const factoryCount = computed(() => {
  return planetStore.countBuildingsOfType(props.buildingType)
})

const activeLimit = computed(() => {
  return gameStore.playerBuildingSettings.get(props.buildingType) || 0
})

const displayCount = computed(() => {
  if (activeLimit.value === 0 && factoryCount.value > 0) {
    return `0 / ${factoryCount.value}`
  }
  if (factoryCount.value > activeLimit.value && activeLimit.value > 0) {
    return `${activeLimit.value} / ${factoryCount.value}`
  }
  return factoryCount.value.toString()
})

const isPaused = computed(() => {
  return factoryCount.value > 0 && activeLimit.value === 0
})

const onRefineryClick = () => {
  gameStore.modalType = 'oreRefinery'
  gameStore.modalData = {
    inputOreLeftId: props.oreLeft,
    inputOreRightId: props.oreRight,
    outputOreId: props.oreProcessed,
    capacity: props.processed.capacity,
    buildingType: props.buildingType
  }
}

</script>

<template>
<div :class="{
          'absolute':true,
          'border':debug
        }"
     :style="'top:' + top + 'rem;left:' + left + 'rem;width:' + width + 'rem;height:' + height + 'rem;'">

  <!-- Level 1 Conveyor -->
  <ConveyorTLBR
    v-if="conveyorType == 'level1'"
    :left="1.25"
    :top="1"
    :height="18"
    :width="2.5"
    :midTime="0.15"
    :min-speed="5"
    :object-image="CDN.icon('ore' + oreLeft)"
    :conversion="conversionLeft"
    :debug="false"
  />

  <ConveyorTRBL
    v-if="conveyorType == 'level1'"
    :left="4.25"
    :top="1"
    :height="18"
    :width="2.5"
    :midTime="0.15"
    :min-speed="5"
    :object-image="CDN.icon('ore' + oreRight)"
    :conversion="conversionRight"
    :debug="false"
  />

  <!-- Level 2 Conveyor -->

  <ConveyorTLBR
    v-if="conveyorType == 'level2'"
    :left="-4.25"
    :top="1"
    :height="18"
    :width="8"
    :midTime="0.3"
    :min-speed="5"
    :object-image="CDN.icon('ore' + oreLeft)"
    :conversion="conversionLeft"
    :debug="false"
  />

  <ConveyorTRBL
    v-if="conveyorType == 'level2'"
    :left="4.25"
    :top="1"
    :height="18"
    :width="8"
    :midTime="0.3"
    :min-speed="5"
    :object-image="CDN.icon('ore' + oreRight)"
    :conversion="conversionRight"
    :debug="false"
  />

  <!-- Level 3 Conveyor -->

  <ConveyorTLBR
    v-if="conveyorType == 'level3'"
    :left="-14.25"
    :top="1"
    :height="18"
    :width="18"
    :midTime="0.5"
    :min-speed="8"
    :object-image="CDN.icon('ore' + oreLeft)"
    :conversion="conversionLeft"
    :debug="false"
  />

  <ConveyorTRBL
    v-if="conveyorType == 'level3'"
    :left="4.25"
    :top="1"
    :height="18"
    :width="18"
    :midTime="0.5"
    :min-speed="8"
    :object-image="CDN.icon('ore' + oreRight)"
    :conversion="conversionRight"
    :debug="false"
  />

  <div :class="{
            'absolute rounded bg-metal-800 border border-metal-700 hover:border-metal-600 cursor-pointer':true,
            'opacity-50':debug
          }"
       style="top:18rem;left:-0.8rem;width:9.6rem;z-index:15"
  >
    <div class="flex-col p-1 cursor-pointer" @click="onRefineryClick">
      <div class="relative">
        <!-- Show grayscale image when no factory or paused -->
        <StyledImage
          v-if="factoryCount == 0 || isPaused"
          :src="CDN.building(128,props.buildingType)"
          size="full"
          :grayscale="true"
        />

        <!-- Show video when factory is active -->
        <video
          v-else
          class="w-full h-full rounded object-cover"
          autoplay
          loop
          muted
          playsinline
          :src="CDN.buildingVideo(128, props.buildingType)"
          alt="">
        </video>

        <!-- No factories overlay -->
        <div v-if="factoryCount == 0" class="absolute inset-0 flex items-center justify-center bg-black/60 rounded-md">
          <span class="text-red-500 text-base text-center leading-tight" style="text-shadow: 0 0 12px rgba(0,0,0,1), 0 0 24px rgba(0,0,0,1), 0 0 36px rgba(0,0,0,0.9), 0 2px 6px rgba(0,0,0,1)">{{ t('resources.factory.noFactories') }}</span>
        </div>

        <!-- Paused overlay -->
        <div v-else-if="isPaused" class="absolute inset-0 flex items-center justify-center bg-black/60 rounded-md">
          <span class="text-yellow-500 text-xs font-semibold text-center leading-tight">{{ t('resources.factory.paused') }}</span>
        </div>

        <!-- Status bar at bottom -->
        <div v-if="factoryCount > 0" class="absolute bottom-0 left-0 right-0 bg-black/60 py-1 text-center rounded-b-md">
          <GenericIcon type="refinery" class="mr-1"/><span :class="isPaused ? 'text-yellow-500' : 'text-green-400'">{{ displayCount }}</span>
        </div>
      </div>
    </div>
  </div>

  <ConveyorTB
    :left="3.5"
    :top="27"
    :height="6"
    :width="2.5"
    :object-image="CDN.icon('ore' + props.oreProcessed)"
    :conversion="processed"
  />

  <Ore
    :ore-id="props.oreProcessed"
    :top="32"
    :left="1.5"
    :debug="debug"
  />

</div>
</template>

<style scoped lang="scss">

</style>